import React, { useState } from 'react';
import { 
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Paper,
  Chip,
  useTheme
} from '@mui/material';
import axios from 'axios';
import api from "../api";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [errors, setErrors] = useState({}); // Initialize as empty object
  const [submitStatus, setSubmitStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const theme = useTheme();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setSubmitStatus(null);
    
    try {
      // const response = await axios.post('http://localhost:5001/api/contact', formData);
      // const response = await axios.post('/api/contact', formData);
      const response = await api.post('/api/contact', formData);
      
      if (response.data.message.includes('but notification failed')) {
        setSubmitStatus('partial');
      } else {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', message: '' });
        setErrors({});
      }
    } catch (err) {
      console.error('Submission error:', err);
      setSubmitStatus('error');
      
      // Handle different error types
      if (err.response) {
        // Server responded with error status
        setErrors({
          form: err.response.data.message || 'Server error occurred'
        });
      } else if (err.request) {
        // Request was made but no response
        setErrors({
          form: 'No response from server. Please check your connection.'
        });
      } else {
        // Other errors
        setErrors({
          form: 'An unexpected error occurred. Please try again.'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Typography 
        variant="h2" 
        component="h1" 
        gutterBottom 
        align="center"
        sx={{ 
          mb: 6,
          fontWeight: 700,
          color: theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.dark
        }}
      >
        Contact Us
      </Typography>
      
      <Paper elevation={3} sx={{ 
        p: 4,
        backgroundColor: theme.palette.background.paper,
        borderRadius: 2
      }}>
        {/* Status Messages */}
        {submitStatus === 'success' && (
          <Box sx={{ 
            mb: 3,
            p: 2,
            backgroundColor: theme.palette.success.light,
            color: theme.palette.success.contrastText,
            borderRadius: 1
          }}>
            <Typography>Your message has been sent successfully!</Typography>
          </Box>
        )}
        
        {submitStatus === 'partial' && (
          <Box sx={{
            mb: 3,
            p: 2,
            backgroundColor: theme.palette.warning.light,
            color: theme.palette.warning.contrastText,
            borderRadius: 1
          }}>
            <Typography>Message received but we couldn't send confirmation</Typography>
          </Box>
        )}
        
        {submitStatus === 'error' && (
          <Box sx={{ 
            mb: 3,
            p: 2,
            backgroundColor: theme.palette.error.light,
            color: theme.palette.error.contrastText,
            borderRadius: 1
          }}>
            <Typography>{errors.form || 'There was an error submitting your message'}</Typography>
          </Box>
        )}

        <Box 
          component="form" 
          onSubmit={handleSubmit} 
          noValidate
          sx={{ mt: 2 }}
        >
          <TextField
            fullWidth
            margin="normal"
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            error={!!errors.name}
            helperText={errors.name}
            required
            variant="outlined"
          />
          
          <TextField
            fullWidth
            margin="normal"
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email}
            required
            variant="outlined"
          />
          
          <TextField
            fullWidth
            margin="normal"
            label="Message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            error={!!errors.message}
            helperText={errors.message}
            multiline
            rows={6}
            required
            variant="outlined"
          />
          
          <Box sx={{ 
            mt: 4,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            {/* <Chip 
              label="Required fields" 
              color="primary" 
              size="small"
              variant="outlined"
            /> */}
            <Button 
              type="submit" 
              variant="contained" 
              size="large"
              disabled={loading}
              sx={{
                px: 4,
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 2
              }}
            >
              {loading ? 'Sending...' : 'Send Message'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default Contact;